import { supabase } from "@/auth/supabase-client";
import {
  ensureFreshSession,
  SUPABASE_EDGE_TIMEOUT_MS,
  withSupabaseTimeout,
} from "@/auth/session-guard";

export interface OutreachDraft {
  subject: string;
  body: string;
  model: string;
  generated_at: string;
}

interface DraftEnvelope {
  data: { draft: OutreachDraft };
}

async function errorMessage(error: unknown): Promise<string> {
  const context = (error as { context?: Response }).context;
  if (context && typeof context.json === "function") {
    try {
      const parsed = (await context.json()) as {
        error?: { message?: string } | string;
        message?: string;
      };
      if (typeof parsed.error === "object" && parsed.error?.message) {
        return parsed.error.message;
      }
      if (typeof parsed.error === "string") return parsed.error;
      if (parsed.message) return parsed.message;
    } catch {
      // Non-JSON body. The SDK message below remains the honest fallback.
    }
  }
  return error instanceof Error ? error.message : String(error);
}

export async function requestOutreachDraft(options: {
  signalId: string;
  contactId?: string;
}): Promise<OutreachDraft> {
  await ensureFreshSession();
  const { data, error } = await withSupabaseTimeout(
    supabase.functions.invoke("draft_signal_outreach", {
      body: {
        signal_id: options.signalId,
        ...(options.contactId ? { contact_id: options.contactId } : {}),
      },
    }),
    "outreach draft request",
    SUPABASE_EDGE_TIMEOUT_MS
  );
  if (error) throw new Error(await errorMessage(error));
  const draft = (data as DraftEnvelope | null)?.data?.draft;
  if (!draft?.subject || !draft.body) {
    throw new Error("The drafting service returned an incomplete draft.");
  }
  return draft;
}
